import {
	TasksSectionWrap,
	SectionTitle,
	TasksList,
	TaskCard,
	TaskIcon,
	TaskInfo,
	TaskDescription,
	TaskReward,
	TaskButton,
} from './TasksSection.styled';
import CurrencyIcon from '../CurrencyIcon';
import { IoChevronForward, IoCheckmarkDoneCircleOutline } from 'react-icons/io5';
import { FaFire } from 'react-icons/fa';
import { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import useUserStore from '../../stores/userStore';

const TasksSection = ({
	user,
	streak,
	adStats,
	subscriptionLoading,
	showSubscribeButton,
	monetagLoading,
	t,
	theme,
	navigate,
	handleRewardIconClick,
	handleShareReferral,
	handleSubscribe,
	handleCheckSubscription,
	handleOpenGroup,
	handleShowAdWithDelay,
	handleShowMonetagWithDelay,
	completedOnly = false
}) => {
	const [voteClicked, setVoteClicked] = useState(false);
	const [showCheckButton, setShowCheckButton] = useState(false);
	const [voteLoading, setVoteLoading] = useState(false);
	const fetchUser = useUserStore(state => state.fetchUser);

	// Проверяем, голосовал ли пользователь уже (из /me)
	const hasVoted = user?.tgVoted || false;

	useEffect(() => {
		if (voteClicked) {
			const timer = setTimeout(() => {
				setShowCheckButton(true);
			}, 5000);
			return () => clearTimeout(timer);
		}
	}, [voteClicked]);

	const handleCheckVote = async () => {
		setVoteLoading(true);
		try {
			const result = await apiService.checkVote();
			// Обновляем данные пользователя после успешной проверки голосования
			await fetchUser();
		} catch (error) {
			console.error('Ошибка при проверке голосования:', error);
		} finally {
			setVoteLoading(false);
		}
	};

	// completedTasks
	const completedTasks = [];
	if (user?.tgSubscribed) {
		completedTasks.push({
			key: 'subscribe',
			icon: '📢',
			desc: t('tasks.subscribe', { app: process.env.REACT_APP_APP_NAME || 'Skin Forge' }),
			reward: '+100',
			extra: <IoCheckmarkDoneCircleOutline size={36} color="green" />
		});
	}
	if (user?.caseOpened) {
		completedTasks.push({
			key: 'open_case',
			icon: '📦',
			desc: t('tasks.open_first_case'),
			reward: '+100',
			extra: <IoCheckmarkDoneCircleOutline size={36} color="green" />
		});
	}
	if (user?.tgVoted) {
		completedTasks.push({
			key: 'vote',
			icon: '🗳️',
			desc: t('tasks.vote'),
			reward: '+100',
			extra: <IoCheckmarkDoneCircleOutline size={36} color="green" />
		});
	}

	// activeTasks
	const activeTasks = [
		{
			key: 'vote',
			icon: '🗳️',
			desc: t('tasks.vote'),
			reward: '+100',
			onClick: () => {
				if (!voteClicked) {
					window.open('https://t.me/tapps_bot/center?startapp=app_skinforge', '_blank');
					setVoteClicked(true);
				}
			},
			extra: (
				hasVoted ? (
					<IoCheckmarkDoneCircleOutline size={36} color="green" />
				) : (
					<TaskButton
						onClick={() => {
							if (!voteClicked) {
								window.open('https://t.me/tapps_bot/center?startapp=app_skinforge', '_blank');
								setVoteClicked(true);
							} else if (showCheckButton && !voteLoading) {
								handleCheckVote();
							}
						}}
						disabled={voteClicked && !showCheckButton || voteLoading}
					>
						{voteLoading ? t('tasks.vote_checking') : voteClicked && showCheckButton ? t('tasks.vote_check') : t('tasks.vote_action')}
						<IoChevronForward size={16} />
					</TaskButton>
				)
			)
		},
		{
			key: 'daily',
			icon: '🎁',
			desc: t('tasks.daily_claim'),
			reward: '+100',
			onClick: handleRewardIconClick,
			extra: (
				<TaskButton as="div" style={{ pointerEvents: 'none', background: 'transparent', color: streak?.currentStreak === 7 ? '#ff9800' : '#2563eb', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
					{t('tasks.streak_label', { days: streak?.currentStreak || 0 })}
					{streak?.currentStreak === 7 && <FaFire color="#ff9800" style={{ marginLeft: 4 }} />}
				</TaskButton>
			)
		},
		{
			key: 'invite',
			icon: '🙋‍♂️',
			desc: t('tasks.invite_friend'),
			reward: '+500',
			onClick: handleShareReferral,
			extra: (
				<TaskButton>
					{t('tasks.invite_action')}
					<IoChevronForward size={16} />
				</TaskButton>
			)
		},
		{
			key: 'subscribe',
			icon: '📢',
			desc: t('tasks.subscribe', { app: process.env.REACT_APP_APP_NAME || 'App' }),
			reward: '+100',
			onClick: user?.tgSubscribed ? handleOpenGroup : (showSubscribeButton ? handleCheckSubscription : handleSubscribe),
			extra: (
				subscriptionLoading ? (
					<TaskButton disabled>{t('common.loading')}</TaskButton>
				) : user?.tgSubscribed ? (
					<IoCheckmarkDoneCircleOutline size={36} color="green" />
				) : showSubscribeButton ? (
					<TaskButton onClick={handleCheckSubscription}>{t('tasks.check')}<IoChevronForward size={16} /></TaskButton>
				) : (
					<TaskButton onClick={handleSubscribe}>{t('tasks.subscribe_action')}<IoChevronForward size={16} /></TaskButton>
				)
			)
		},
		{
			key: 'open_case',
			icon: '📦',
			desc: t('tasks.open_first_case'),
			reward: '+100',
			onClick: user && !user.caseOpened ? () => navigate('/cases') : undefined,
			extra: user && !user.caseOpened ? (
				<TaskButton onClick={() => navigate('/cases')}>{t('tasks.open_action')}<IoChevronForward size={16} /></TaskButton>
			) : (
				<IoCheckmarkDoneCircleOutline size={36} color="green" />
			)
		},

		{
			key: 'watch_ad_reward_adsgram',
			icon: '📺',
			desc: t('tasks.watch_ad'),
			reward: '+15',
			onClick: adStats && adStats.adsgram && adStats.adsgram.watched < adStats.adsgram.limit ? handleShowAdWithDelay : undefined,
			extra: adStats && adStats.adsgram ? (
				adStats.adsgram.watched >= adStats.adsgram.limit ? (
					<TaskButton disabled style={{ color: '#666', cursor: 'default' }}>
						{t('tasks.ad_limit_reached')}
					</TaskButton>
				) : (
					<TaskButton onClick={handleShowAdWithDelay}>
						{adStats.adsgram.watched}/{adStats.adsgram.limit} {t('tasks.watch_ad_action')}
						<IoChevronForward size={16} />
					</TaskButton>
				)
			) : (
				<TaskButton disabled>{t('common.loading')}</TaskButton>
			)
		},

		// monetag_interstitial (после Adsgram)
		...(adStats && adStats.monetag && adStats.monetag.watched < adStats.monetag.limit ? [
			{
				key: 'monetag_interstitial',
				icon: '🎯',
				desc: t('tasks.watch_ad'),
				reward: '+5',
				onClick: handleShowMonetagWithDelay,
				extra: monetagLoading ? (
					<TaskButton disabled>{t('common.loading')}</TaskButton>
				) : (
					<TaskButton onClick={handleShowMonetagWithDelay}>
						{t('tasks.monetag_interstitial_action')}
						<IoChevronForward size={16} />
					</TaskButton>
				)
			}
		] : []),
	];

	// Фильтруем активные задания: подписка и открытие кейса, если уже выполнены, не показываем
	const filteredActiveTasks = activeTasks.filter(task => {
		if (task.key === 'open_case' && user?.caseOpened) return false;
		if (task.key === 'subscribe' && user?.tgSubscribed) return false;
		if (task.key === 'vote' && hasVoted) return false;
		if (task.key === 'watch_ad') return true;
		if (task.key === 'monetag_interstitial') return true;
		return true;
	});

	return (
		<TasksSectionWrap>
			{!completedOnly && (
				<>
					<SectionTitle theme={theme}>{t('tasks.title')}</SectionTitle>
					<TasksList>
						{filteredActiveTasks.map(task => (
							<TaskCard key={task.key} onClick={task.onClick ? (e) => {
								if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.HapticFeedback) {
									window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
								}
								if (typeof task.onClick === 'function') {
									task.onClick(e);
								}
							} : undefined} style={{ cursor: task.onClick ? 'pointer' : 'default' }} theme={theme}>
								<TaskIcon theme={theme}>{task.icon}</TaskIcon>
								<TaskInfo>
									<TaskDescription theme={theme}>{task.desc}</TaskDescription>
									<TaskReward theme={theme}>{task.reward} <CurrencyIcon size={16} /></TaskReward>
								</TaskInfo>
								{task.extra}
							</TaskCard>
						))}
					</TasksList>
				</>
			)}
			{completedOnly && completedTasks.length > 0 && (
				<>
					<SectionTitle theme={theme} style={{ marginTop: 32 }}>{t('tasks.completed')}</SectionTitle>
					<TasksList>
						{completedTasks.map(task => (
							<TaskCard
								key={task.key}
								style={{ cursor: task.key === 'subscribe' ? 'pointer' : 'default' }}
								onClick={task.key === 'subscribe' ? (e) => {
									if (window.Telegram && window.Telegram.WebApp && window.Telegram.WebApp.HapticFeedback) {
										window.Telegram.WebApp.HapticFeedback.impactOccurred('light');
									}
									if (typeof task.onClick === 'function') {
										task.onClick(e);
									}
								} : undefined}
								theme={theme}
							>
								<TaskIcon theme={theme}>{task.icon}</TaskIcon>
								<TaskInfo>
									<TaskDescription theme={theme}>{task.desc}</TaskDescription>
									<TaskReward theme={theme}>{task.reward} <CurrencyIcon size={16} /></TaskReward>
								</TaskInfo>
								{task.extra}
							</TaskCard>
						))}
					</TasksList>
				</>
			)}
		</TasksSectionWrap>
	);
};

export default TasksSection;

// Новый компонент только для completed
export const CompletedTasksSection = (props) => <TasksSection {...props} completedOnly={true} />; 